// Smart guides for wall items (Figma-style "smart alignment guides").
//
// While a painting/window is dragged, its edges and centre are compared with
// every other wall item: equal heights are matched across BOTH walls (height on
// the wall is the same in the world), positions along the wall only on the SAME
// wall. A match within a few screen px snaps the item onto it (a light magnet)
// and shows a guide line.
//
// Coordinates are "wall coordinates" in room-local px:
//   u — distance along the wall from the room corner (x on the right wall, −x on
//       the left; x = 0 is the corner — a frame's depth may poke slightly past it);
//   v — height above the wall's base line (+ = up), base line y = |x| · WALL_SLOPE.
// A slanted sprite box maps to a wall box by taking its bottom at the far end and
// its top at the near end (the two corners that lie on its bottom/top lines).
import { HALF_TILE_WIDTH, MAP_WIDTH_TILES } from './grid';
import { WALL_HEIGHT_PX, WALL_SLOPE } from './windowSnap';

export type WallSide = 'left' | 'right';

/** Length of each wall along x (room-local px): corner at 0, far end at ±WALL_LENGTH_PX. */
export const WALL_LENGTH_PX = MAP_WIDTH_TILES * HALF_TILE_WIDTH;

/** Magnet reach in SCREEN px — divided by the zoom so it feels the same at every zoom. */
export const WALL_GUIDE_SNAP_SCREEN_PX = 3;

export interface WallBox {
  side: WallSide;
  /** Along the wall: near (corner) and far edge. */
  u0: number;
  u1: number;
  /** Height on the wall: bottom and top edge. */
  v0: number;
  v1: number;
}

/** Room-local sprite box → wall box. */
export function wallBoxFromBounds(
  side: WallSide,
  b: { x0: number; y0: number; x1: number; y1: number },
): WallBox {
  const ua = side === 'left' ? -b.x0 : b.x0;
  const ub = side === 'left' ? -b.x1 : b.x1;
  const u0 = Math.min(ua, ub);
  const u1 = Math.max(ua, ub);
  return { side, u0, u1, v0: u1 * WALL_SLOPE - b.y1, v1: u0 * WALL_SLOPE - b.y0 };
}

/** A guide line to draw: a height across whole walls, or a position along one wall. */
export type WallGuide =
  | { axis: 'v'; value: number; sides: WallSide[] }
  | { axis: 'u'; value: number; side: WallSide };

export interface WallGuideResult {
  /** Shift to apply to the moving item (0 when nothing is in reach). */
  du: number;
  dv: number;
  /** Guides that match after the shift. */
  guides: WallGuide[];
}

/**
 * Lines of a box on one axis, grouped by what may align with what: edges with
 * edges (top↔top, bottom↔top, left↔right…) and centre with centre. Edge↔centre
 * is left out on purpose — with several items on the walls it made the magnet
 * catch every few pixels and fight ordinary movement.
 */
const lineGroups = (a: number, b: number): number[][] => [[a, b], [(a + b) / 2]];

/** Nearest match between two boxes' lines within `reach`: the shift moving → target, or null. */
function nearestShift(mine: number[][], theirs: number[][], reach: number): number | null {
  let best: number | null = null;
  mine.forEach((group, i) => {
    for (const m of group) {
      for (const t of theirs[i]) {
        const d = t - m;
        if (Math.abs(d) <= reach && (best === null || Math.abs(d) < Math.abs(best))) best = d;
      }
    }
  });
  return best;
}

/** The lines of `mine` that sit on one of `theirs` (same group). */
function matchedLines(mine: number[][], theirs: number[][]): number[] {
  return mine.flatMap((group, i) => group.filter((m) => theirs[i].some((t) => Math.abs(t - m) < MATCH_EPS)));
}

const MATCH_EPS = 0.5;

/**
 * Find the snap for `moving` against `others`. `reach` is in room px (screen
 * px ÷ zoom).
 */
export function findWallGuides(moving: WallBox, others: readonly WallBox[], reach: number): WallGuideResult {
  const mineV = lineGroups(moving.v0, moving.v1);
  const mineU = lineGroups(moving.u0, moving.u1);

  let dv: number | null = null;
  let du: number | null = null;
  for (const o of others) {
    const v = nearestShift(mineV, lineGroups(o.v0, o.v1), reach);
    if (v !== null && (dv === null || Math.abs(v) < Math.abs(dv))) dv = v;
    if (o.side !== moving.side) continue;
    const u = nearestShift(mineU, lineGroups(o.u0, o.u1), reach);
    if (u !== null && (du === null || Math.abs(u) < Math.abs(du))) du = u;
  }

  const shifted: WallBox = {
    ...moving,
    u0: moving.u0 + (du ?? 0),
    u1: moving.u1 + (du ?? 0),
    v0: moving.v0 + (dv ?? 0),
    v1: moving.v1 + (dv ?? 0),
  };
  return { du: du ?? 0, dv: dv ?? 0, guides: matchingGuides(shifted, others) };
}

/** Every guide `box` currently sits on (used after the snap, and after clamping). */
export function matchingGuides(box: WallBox, others: readonly WallBox[]): WallGuide[] {
  const mineV = lineGroups(box.v0, box.v1);
  const mineU = lineGroups(box.u0, box.u1);
  const byHeight = new Map<number, Set<WallSide>>();
  const along = new Set<number>();
  for (const o of others) {
    for (const v of matchedLines(mineV, lineGroups(o.v0, o.v1))) {
      if (!byHeight.has(v)) byHeight.set(v, new Set([box.side]));
      byHeight.get(v)!.add(o.side);
    }
    if (o.side === box.side) for (const u of matchedLines(mineU, lineGroups(o.u0, o.u1))) along.add(u);
  }
  const guides: WallGuide[] = [];
  for (const [value, sides] of [...byHeight].sort((a, b) => a[0] - b[0])) guides.push({ axis: 'v', value, sides: [...sides] });
  for (const value of [...along].sort((a, b) => a - b)) guides.push({ axis: 'u', value, side: box.side });
  return guides;
}

/** A stable key of the guide set — a change means "just snapped" (haptic tick). */
export function wallGuidesKey(guides: readonly WallGuide[]): string {
  return guides
    .map((g) => (g.axis === 'v' ? `v${Math.round(g.value)}` : `u${g.side}${Math.round(g.value)}`))
    .join('|');
}

/** Room-local segment of a guide on one wall: horizontal guides span the whole wall,
 *  vertical ones run from the floor line to the wall top. */
export function wallGuideSegment(
  g: WallGuide,
  side: WallSide,
): { x0: number; y0: number; x1: number; y1: number } {
  const sx = side === 'left' ? -1 : 1;
  if (g.axis === 'v') {
    return { x0: 0, y0: -g.value, x1: sx * WALL_LENGTH_PX, y1: WALL_LENGTH_PX * WALL_SLOPE - g.value };
  }
  const base = g.value * WALL_SLOPE;
  return { x0: sx * g.value, y0: base, x1: sx * g.value, y1: base - WALL_HEIGHT_PX };
}
